import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { DashboardListItem } from './DashboardListItem';
import * as ScrollArea from '@radix-ui/react-scroll-area';

const getVariantStyles = (variant: 'today' | 'overdue') => {
  switch (variant) {
    case 'today':
      return {
        statusBg: 'bg-(--secondary-B100)! text-(--secondary-B1000)!',
      };
    case 'overdue':
      return {
        statusBg: 'bg-(--danger-D100)! text-(--danger-D700)!',
      };
  }
};

const getTypeBadge = (type?: 'hearing' | 'project') => {
  switch (type) {
    case 'hearing':
      return 'bg-(--secondary-B100) text-(--secondary-B1000)';
    case 'project':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusBg = (statusColor?: string, fallback?: string): string => {
  switch (statusColor) {
    case 'red':
      return 'bg-(--danger-D100) text-(--danger-D700)';
    case 'yellow':
      return 'bg-(--tertiary-Y100) text-(--tertiary-Y600)';
    case 'blue':
      return 'bg-(--secondary-B100) text-(--secondary-B1000)';
    case 'purple':
      return 'bg-purple-100 text-purple-700';
    default:
      return fallback ?? 'bg-gray-100 text-gray-700';
  }
};

interface DashboardListItem {
  id: string;
  title: string;
  subtitle: string;
  status?: string;
  statusColor?: 'blue' | 'purple' | 'red' | 'yellow';
  type?: 'hearing' | 'project';
  onClick?: () => void;
}

interface DashboardListProps {
  title: string;
  items: DashboardListItem[];
  variant: 'today' | 'overdue';
  className?: string;
}

export const DashboardList: FC<DashboardListProps> = ({
  title,
  items,
  variant,
  className = '',
}) => {
  const styles = getVariantStyles(variant);

  return (
    <div
      className={`border bg-(--neutral-white-NW1000) border-border rounded-xl shadow-sm ${className} h-full`}
    >
      <div
        className={`border-b border-gray-200 px-4 py-5 sm:px-6 rounded-t-xl ${variant === 'overdue' ? 'bg-(--danger-D100) border-(--danger-D200)' : 'bg-(--neutral-white-NW1000)'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {variant === 'overdue' && <AlertTriangle className="w-5 h-5 text-(--danger-D700)" />}
            <span
              className={`[font-size:var(--fs-l3)] font-semibold ${variant === 'overdue' ? 'text-(--danger-D700)!' : 'text-(--neutral-black-NB1000)!'}`}
            >
              {title}
            </span>
          </div>
          {variant === 'today' && (
            <span className="text-sm text-gray-500">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>

      <ScrollArea.Root className="max-h-96 h-full overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full overflow-scroll">
          {items.length === 0 ? (
            <EmptyState
              message="No items found"
              description={
                variant === 'today' ? 'No items scheduled for today' : 'No overdue items'
              }
            />
          ) : (
            <ul className={`divide-y`}>
              {items.map((item) => (
                <DashboardListItem
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  subtitle={item.subtitle}
                  type={item.type}
                  status={item.status}
                  typeBadge={item.type ? getTypeBadge(item.type) : undefined}
                  statusBg={
                    item.status ? getStatusBg(item.statusColor, styles.statusBg) : undefined
                  }
                  isOverdue={variant === 'overdue'}
                  onClick={item.onClick}
                />
              ))}
            </ul>
          )}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex w-2.5 bg-transparent p-0.5 hover:bg-gray-100 transition-colors"
        >
          <ScrollArea.Thumb className="flex-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </ScrollArea.Scrollbar>
        <ScrollArea.Scrollbar
          orientation="horizontal"
          className="flex h-2.5 bg-transparent p-0.5 hover:bg-gray-100 transition-colors"
        >
          <ScrollArea.Thumb className="flex-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </ScrollArea.Scrollbar>
        <ScrollArea.Corner className="bg-gray-100" />
      </ScrollArea.Root>
    </div>
  );
};
